import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Save, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .trim()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 120);

const today = () => new Date().toISOString().slice(0, 10);

const empty = {
  slug: "",
  title: "",
  excerpt: "",
  meta_description: "",
  keywords: "",
  tags: "",
  author: "Bella — Beauty & Balance",
  published_at: today(),
  read_minutes: 5,
  hero_image: "",
  content_html: "<p>Start writing…</p>",
  faq_text: "",
};

const faqToText = (arr) =>
  (arr || []).map((f) => `Q: ${f.q || ""}\nA: ${f.a || ""}`).join("\n\n");

const textToFaq = (txt) => {
  if (!txt.trim()) return [];
  const blocks = txt.split(/\n\s*\n+/);
  const out = [];
  for (const b of blocks) {
    const qm = b.match(/^Q:\s*(.+?)\s*(?:\n|$)/im);
    const am = b.match(/^A:\s*([\s\S]+?)\s*$/im);
    if (qm && am) out.push({ q: qm[1].trim(), a: am[1].trim() });
  }
  return out;
};

export default function AdminBlogEditor() {
  const { slug } = useParams();
  const isEdit = !!slug;
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [originalSlug] = useState(slug || "");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/blog/${slug}`, { withCredentials: true });
        const p = res.data;
        setForm({
          slug: p.slug || "",
          title: p.title || "",
          excerpt: p.excerpt || "",
          meta_description: p.meta_description || "",
          keywords: (p.keywords || []).join(", "),
          tags: (p.tags || []).join(", "),
          author: p.author || "Bella — Beauty & Balance",
          published_at: p.published_at || today(),
          read_minutes: p.read_minutes || 5,
          hero_image: p.hero_image || "",
          content_html: p.content_html || "",
          faq_text: faqToText(p.faq),
        });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load post");
        navigate("/admin/blog", { replace: true });
      } finally { setLoading(false); }
    })();
  }, [isEdit, slug, navigate]);

  const onChange = (field) => (e) => {
    const val = e.target.type === "number" ? Number(e.target.value) : e.target.value;
    setForm((f) => ({ ...f, [field]: val }));
  };

  const onTitleChange = (e) => {
    const title = e.target.value;
    setForm((f) => ({
      ...f,
      title,
      slug: isEdit ? f.slug : slugify(title),
    }));
  };

  const buildPayload = () => ({
    slug: form.slug,
    title: form.title,
    excerpt: form.excerpt,
    meta_description: form.meta_description,
    keywords: form.keywords.split(",").map((k) => k.trim()).filter(Boolean),
    tags: form.tags.split(",").map((k) => k.trim()).filter(Boolean),
    author: form.author,
    published_at: form.published_at,
    read_minutes: Number(form.read_minutes) || 5,
    hero_image: form.hero_image,
    content_html: form.content_html,
    faq: textToFaq(form.faq_text),
  });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = buildPayload();
      if (isEdit) {
        const { slug: _s, ...patch } = payload;
        await axios.put(`${API}/admin/blog/${originalSlug}`, patch, { withCredentials: true });
        toast.success("Post updated");
      } else {
        await axios.post(`${API}/admin/blog`, payload, { withCredentials: true });
        toast.success("Post created");
      }
      navigate("/admin/blog");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const onDelete = async () => {
    if (!isEdit) return;
    if (!window.confirm(`Delete "${originalSlug}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/blog/${originalSlug}`, { withCredentials: true });
      toast.success("Post deleted");
      navigate("/admin/blog");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (loading) return <p className="text-[#0F1F4E]/70">Loading…</p>;

  return (
    <section data-testid="admin-blog-editor">
      <Link
        to="/admin/blog"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-[#0F1F4E]/70 hover:text-[#0F1F4E] mb-6"
      >
        <ArrowLeft className="w-4 h-4" /> All posts
      </Link>

      <h2 className="font-serif text-3xl md:text-4xl font-light tracking-tight mb-8">
        {isEdit ? "Edit post" : "New post"}
      </h2>

      <form onSubmit={onSubmit} className="space-y-6 max-w-4xl">
        <Field label="Title">
          <input
            required
            data-testid="editor-title"
            value={form.title}
            onChange={onTitleChange}
            className="w-full px-4 py-3 border border-[#D9CFA0] rounded-md bg-white font-serif text-xl"
          />
        </Field>

        <Field label="Slug (URL)">
          <input
            required
            data-testid="editor-slug"
            value={form.slug}
            onChange={onChange("slug")}
            disabled={isEdit}
            className="w-full px-4 py-3 border border-[#D9CFA0] rounded-md bg-white font-mono text-sm disabled:bg-[#F2EBD3] disabled:text-[#0F1F4E]/60"
          />
          {isEdit && <p className="text-xs text-[#0F1F4E]/60 mt-1">Slug cannot be changed after creation.</p>}
        </Field>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Published date (YYYY-MM-DD)">
            <input
              required
              data-testid="editor-published"
              value={form.published_at}
              onChange={onChange("published_at")}
              className="w-full px-4 py-3 border border-[#D9CFA0] rounded-md bg-white"
            />
          </Field>
          <Field label="Read time (minutes)">
            <input
              required
              type="number"
              min="1"
              max="120"
              data-testid="editor-read-minutes"
              value={form.read_minutes}
              onChange={onChange("read_minutes")}
              className="w-full px-4 py-3 border border-[#D9CFA0] rounded-md bg-white"
            />
          </Field>
        </div>

        <Field label="Hero image URL">
          <input
            data-testid="editor-hero"
            value={form.hero_image}
            onChange={onChange("hero_image")}
            placeholder="https://images.unsplash.com/…"
            className="w-full px-4 py-3 border border-[#D9CFA0] rounded-md bg-white"
          />
          {form.hero_image && (
            <img src={form.hero_image} alt="" className="mt-3 rounded-md border border-[#D9CFA0] max-h-48 object-cover" />
          )}
        </Field>

        <Field label="Excerpt (shown in blog index)">
          <textarea
            required
            rows={3}
            data-testid="editor-excerpt"
            value={form.excerpt}
            onChange={onChange("excerpt")}
            className="w-full px-4 py-3 border border-[#D9CFA0] rounded-md bg-white"
          />
        </Field>

        <Field label="Meta description (SEO)">
          <textarea
            rows={2}
            data-testid="editor-meta"
            value={form.meta_description}
            onChange={onChange("meta_description")}
            className="w-full px-4 py-3 border border-[#D9CFA0] rounded-md bg-white"
          />
        </Field>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Keywords (comma-separated)">
            <input
              data-testid="editor-keywords"
              value={form.keywords}
              onChange={onChange("keywords")}
              className="w-full px-4 py-3 border border-[#D9CFA0] rounded-md bg-white"
            />
          </Field>
          <Field label="Tags (comma-separated)">
            <input
              data-testid="editor-tags"
              value={form.tags}
              onChange={onChange("tags")}
              className="w-full px-4 py-3 border border-[#D9CFA0] rounded-md bg-white"
            />
          </Field>
        </div>

        <Field label="Author">
          <input
            data-testid="editor-author"
            value={form.author}
            onChange={onChange("author")}
            className="w-full px-4 py-3 border border-[#D9CFA0] rounded-md bg-white"
          />
        </Field>

        <Field label="Content (HTML)">
          <textarea
            required
            rows={18}
            data-testid="editor-content"
            value={form.content_html}
            onChange={onChange("content_html")}
            className="w-full px-4 py-3 border border-[#D9CFA0] rounded-md bg-white font-mono text-xs"
          />
          <p className="text-xs text-[#0F1F4E]/60 mt-1">
            Plain HTML — supports &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;a&gt;, &lt;blockquote&gt;, &lt;strong&gt;, &lt;em&gt;.
          </p>
        </Field>

        <Field label='FAQ (one per block: "Q: …" then "A: …", separated by blank lines)'>
          <textarea
            rows={8}
            data-testid="editor-faq"
            value={form.faq_text}
            onChange={onChange("faq_text")}
            className="w-full px-4 py-3 border border-[#D9CFA0] rounded-md bg-white font-mono text-xs"
            placeholder={"Q: Where is the studio?\nA: Downtown Sacramento, by appointment."}
          />
        </Field>

        <div className="flex flex-wrap items-center gap-4 pt-4">
          <button
            type="submit"
            disabled={saving}
            data-testid="editor-save-btn"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0F1F4E] text-[#F8F1D8] text-xs uppercase tracking-wider hover:bg-[#142a66] disabled:opacity-60"
          >
            <Save className="w-4 h-4" /> {saving ? "Saving…" : isEdit ? "Save changes" : "Create post"}
          </button>
          {isEdit && (
            <button
              type="button"
              onClick={onDelete}
              data-testid="editor-delete-btn"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-red-600 border border-red-200 text-xs uppercase tracking-wider hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          )}
        </div>
      </form>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-[#0F1F4E]/70 mb-2">{label}</span>
      {children}
    </label>
  );
}
